import {
  ChangeEvent,
  Dispatch,
  FC,
  FormEvent,
  forwardRef,
  Fragment,
  SetStateAction,
  useCallback,
  useContext,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react';

import { useOutsideClickHandler } from 'hooks';
import useTimeZone from 'hooks/use-timezone';
import { Redelivery, RedeliveryDetail } from 'types';

import './gate-redelivery.scss';
import { GateRedeliveryContext } from './redelivery-context';

export interface GateRedeliveryRef {
  openDialog: (redelivery: Redelivery, onSubmit?: () => void | Promise<void>) => void;
}

interface RedeliveryDetailProps {
  redeliveryDetail: RedeliveryDetail;
  setRedeliveryDetail: Dispatch<SetStateAction<RedeliveryDetail>>;
}

const GateRedeliveryDetail: FC<RedeliveryDetailProps> = ({ redeliveryDetail, setRedeliveryDetail }) => {
  /* prettier-ignore */
  const handleChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setRedeliveryDetail(prevState => ({ ...prevState, count: Math.max(1, Number(e.target.value)) }))
  }, [setRedeliveryDetail]);

  /* prettier-ignore */
  const handleStep = useCallback((step: -1 | 1) => () => {
    setRedeliveryDetail(prevState => ({ ...prevState, count: Math.max(1, (prevState.count ?? 0) + step) }))
  }, [setRedeliveryDetail]);

  return (
    <Fragment>
      <label htmlFor={`${redeliveryDetail.sizeType}-size-type`}>QUANTITY</label>
      <div className="flex-grow-2 p-relative quantity-stepper g-3">
        <button type="button" onClick={handleStep(-1)} data-cy="decrease-redelivery-quantity-button">
          <span className="material-icons">remove</span>
        </button>
        <input
          id={`${redeliveryDetail.sizeType}-size-type`}
          inputMode="numeric"
          value={redeliveryDetail.count ?? 0}
          onChange={handleChange}
          onFocus={e => e.target.setSelectionRange(0, e.target.value.length)}
        />
        <button type="button" onClick={handleStep(1)} data-cy="increase-redelivery-quantity-button">
          <span className="material-icons">add</span>
        </button>
        <label className={`p-absolute l-100 text-info-main `}>&times;&nbsp;{redeliveryDetail.sizeType}</label>
      </div>
    </Fragment>
  );
};
const GateRedelivery = forwardRef<GateRedeliveryRef>((_, forwardedRef) => {
  const { dateInputToUtc, newZonedDayJs, utcToZonedDate } = useTimeZone();
  const { setGateRedelivery } = useContext(GateRedeliveryContext);

  const contentRef = useRef<HTMLDivElement>(null);
  const dialogRef = useRef<HTMLDialogElement>(null);

  const [redelivery, setRedelivery] = useState<Partial<Redelivery>>({});
  const [redeliveryDetails, setRedeliveryDetails] = useState<Map<string, RedeliveryDetail>>(new Map());
  const [onSubmit, setOnSubmit] = useState<() => void>(() => {});

  const redeliveryDetailProps = useMemo(() => {
    return [...redeliveryDetails.entries()].map(([sizeType, redeliveryDetail]) => ({
      redeliveryDetail,
      setRedeliveryDetail: (action: SetStateAction<RedeliveryDetail>) => {
        setRedeliveryDetails(prevState =>
          new Map(prevState).set(
            sizeType,
            typeof action === 'function' ? action({ ...prevState.get(sizeType) } as any) : action
          )
        );
      }
    }));
  }, [redeliveryDetails]);

  const closeDialog = () => {
    if (!dialogRef.current?.open) return;
    setRedelivery({});
    setOnSubmit(() => () => {});
    dialogRef.current?.close();
  };

  useOutsideClickHandler(contentRef, closeDialog);

  useImperativeHandle(
    forwardedRef,
    () => ({
      openDialog: (redelivery: Redelivery, onSubmit?: () => void) => {
        if (dialogRef.current?.open) return;
        setRedelivery({
          ...redelivery,
          startDate: redelivery.startDate ?? newZonedDayJs().toISOString(),
          expirationDate: redelivery.expirationDate ?? newZonedDayJs().add(1, 'month').toISOString()
        });
        setRedeliveryDetails(
          Object.assign([], redelivery.redeliveryDetails).reduce(
            (acc, val: RedeliveryDetail) => acc.set(val.sizeType, val),
            new Map()
          )
        );
        setOnSubmit(() => () => onSubmit?.());
        dialogRef.current?.showModal();
      }
    }),
    [newZonedDayJs]
  );

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setGateRedelivery({ ...redelivery, redeliveryDetails: [...redeliveryDetails.values()] });

    onSubmit?.();
    closeDialog();
  };

  return (
    <dialog ref={dialogRef}>
      <div ref={contentRef}>
        <form
          className="d-flex flex-column"
          data-cy="gate-redelivery-form"
          id="gate-redelivery"
          onReset={closeDialog}
          onSubmit={handleSubmit}
        >
          <h3 className="text-primary-dark">{!redelivery?.id ? 'CREATE REDELIVERY' : 'EDIT REDELIVERY'}</h3>
          <label htmlFor="redelivery-number">REDELIVERY</label>
          <input id="redelivery-number" type="text" disabled value={redelivery?.redeliveryNumber ?? ''} />
          <label htmlFor="customer">CUSTOMER</label>
          <input id="customer" type="text" disabled value={redelivery?.customer?.name ?? ''} />
          <label htmlFor="location">LOCATION</label>
          <input id="location" type="text" disabled value={redelivery?.location?.name ?? ''} />
          <label htmlFor="start-date">START DATE</label>
          <input id="start-date" type="date" disabled value={utcToZonedDate(redelivery?.startDate)} />
          <label htmlFor="expiration-date">EXPIRATION</label>
          <input
            type="date"
            id="expiration-date"
            disabled={!!redelivery?.id}
            value={utcToZonedDate(redelivery?.expirationDate)}
            onChange={e =>
              setRedelivery(prevState => prevState && { ...prevState, expirationDate: dateInputToUtc(e.target.value) })
            }
          />
          {redeliveryDetailProps.map(props => (
            <GateRedeliveryDetail key={props.redeliveryDetail.sizeType} {...props} />
          ))}
          <label htmlFor="notes">NOTES</label>
          <textarea
            id="notes"
            value={redelivery?.notes ?? ''}
            onChange={e => setRedelivery(prevState => prevState && { ...prevState, notes: e.target.value })}
          />
          <div className="d-flex justify-content-end mt-4">
            <div className="grid-2">
              <button type="reset" className="btn-outline">
                reset
              </button>
              <button type="submit" className="btn-primary">
                submit
              </button>
            </div>
          </div>
        </form>
      </div>
    </dialog>
  );
});

export default GateRedelivery;
